import { BulkUploadResultStatus, BulkUploadStatus } from "../constants/constants";

/**
 * One row's failure from a bulk user CSV upload.
 *
 * `reason` is a short, admin-readable explanation and `detail` the underlying error message. Rows
 * recorded before per-row reasons existed carry an empty `reason` — the API normalizes those
 * legacy entries (which were stored as bare email strings) into this shape server-side, so only
 * objects ever arrive on the wire.
 */
export class BulkUploadFailedEntry {
    constructor(entry: any) {
        // Normalized like every other optional field on the wire: the API emits explicit nulls, and
        // a null here would sit in a field typed `string | undefined` and lie about its own type.
        this.email = entry?.Email ?? undefined;
        this.reason = entry?.Reason ?? undefined;
        this.detail = entry?.Detail ?? undefined;
    }

    email?: string;
    reason?: string;
    detail?: string;
}

/**
 * Wire shape of a bulk user CSV upload status row returned by
 * `GET /api/organizations/{orgId}/users/bulk/uploads` and, for a single row,
 * `GET /api/organizations/{orgId}/users/bulk/uploads/{fileId}`. Both endpoints return the same
 * `BulkUploadStatusResponse` DTO.
 *
 * The handler serializes with `JsonConvert.SerializeObject` using default Newtonsoft settings, so
 * DTO properties arrive PascalCase and nulls ARE emitted. Only members carrying an explicit
 * `[JsonProperty]` attribute are lowercase — the `items` / `totalItemCount` envelope and the
 * per-item `links` collection. This matches the convention used by `Invitation.ts`.
 *
 * Unprocessed uploads send the result fields as explicit `null`, which we normalize to
 * `undefined` so downstream code only has one "no value" case to handle.
 *
 * Every field is declared optional because the constructor reads through optional chaining and so
 * tolerates a missing payload — declaring them required would let callers dereference a value the
 * runtime never guaranteed.
 */
export class BulkUpload {
    constructor(bulkUpload: any) {
        this.fileIdentifier = bulkUpload?.FileIdentifier;
        this.organizationId = bulkUpload?.OrganizationId;
        this.originalFileName = bulkUpload?.OriginalFileName;
        this.uploaderEmail = bulkUpload?.UploaderEmail;
        this.uploadedAt = bulkUpload?.UploadedAt;
        this.status = bulkUpload?.Status;
        this.resultStatus = bulkUpload?.ResultStatus ?? undefined;
        this.recordCount = bulkUpload?.RecordCount ?? undefined;
        this.processedRowCount = bulkUpload?.ProcessedRowCount ?? undefined;
        this.successCount = bulkUpload?.SuccessCount ?? undefined;
        this.failedCount = bulkUpload?.FailedCount ?? undefined;
        // Guarded on Array: the column is free-form JSON server-side, and a non-array would
        // otherwise blow up on `.map` rather than degrading to "no failures recorded".
        this.failedEmails = Array.isArray(bulkUpload?.FailedEmails)
            ? bulkUpload.FailedEmails.map((entry: any) => new BulkUploadFailedEntry(entry))
            : undefined;
        this.failedEmailsRetained = bulkUpload?.FailedEmailsRetained ?? undefined;
        this.startTime = bulkUpload?.StartTime ?? undefined;
        this.endTime = bulkUpload?.EndTime ?? undefined;
        this.appliedUserGroupIds = bulkUpload?.AppliedUserGroupIds ?? undefined;
        this.rejectedUserGroupIds = bulkUpload?.RejectedUserGroupIds ?? undefined;
    }

    fileIdentifier?: string;
    organizationId?: string;
    originalFileName?: string;
    uploaderEmail?: string;
    uploadedAt?: string;
    status?: BulkUploadStatus;
    resultStatus?: BulkUploadResultStatus;
    recordCount?: number;
    processedRowCount?: number;
    successCount?: number;
    failedCount?: number;
    /**
     * The failures the job retained. Bounded independently of `failedCount`, which stays the
     * authoritative total — see `failedEmailsRetained`.
     */
    failedEmails?: BulkUploadFailedEntry[];
    /**
     * How many entries `failedEmails` actually holds after bounding. When this is below
     * `failedCount` the list is a truncated sample, not the whole set.
     */
    failedEmailsRetained?: number;
    /** UTC time processing began. Null until the blob-trigger job records it. */
    startTime?: string;
    /** UTC time processing reached a terminal status. Null until the blob-trigger job records it. */
    endTime?: string;
    /** Target user groups that were valid at process time and applied to every processed user. */
    appliedUserGroupIds?: string[];
    /** Target user groups rejected at process time (deleted, cross-org, or a disallowed type). */
    rejectedUserGroupIds?: string[];
}
